import { Button, ButtonGroup, DropdownMenu } from '@components'
import React, { useLayoutEffect } from 'react'
import { FaEllipsisH } from 'react-icons/fa'
import { FaXmark } from 'react-icons/fa6'
import styled from 'styled-components'
import { useFloatingDetailControls } from './Layouts/DoubleColumnLayout'

interface MenuItemProps {
  Icon?: any
  name?: string
  onClick?: () => any
  intent?: any
  disabled?: boolean
  /** Items with the same group share one block (no divider between them). */
  group?: string
}

interface Props {
  header?: string | React.ReactNode
  meta?: React.ReactNode
  subHeader?: React.ReactNode
  menuItems?: MenuItemProps[]
  contentInset?: 'md'
  /** Bottom border + detail-panel title weight/size (invoice detail refresh). */
  withDivider?: boolean
  /** Status badges/chips rendered in their own row under the meta line. */
  badges?: React.ReactNode
}

const buildDropdownItems = (menuItems?: MenuItemProps[]) => {
  const visibleItems = menuItems?.filter(item => item?.name) ?? []
  if (visibleItems.length === 0) return []

  const groups: MenuItemProps[][] = []
  let currentGroup: MenuItemProps[] = []
  let currentGroupKey: string | undefined

  for (const item of visibleItems) {
    const groupKey = item.group
    if (currentGroup.length > 0 && groupKey != null && groupKey === currentGroupKey) {
      currentGroup.push(item)
      continue
    }

    if (currentGroup.length > 0) {
      groups.push(currentGroup)
    }
    currentGroup = [item]
    currentGroupKey = groupKey
  }

  if (currentGroup.length > 0) {
    groups.push(currentGroup)
  }

  return groups.map((group, groupIndex) => {
    const buttons = group.map(
      ({ Icon, name, onClick, intent = 'none', disabled = false }, index) => (
        <Button
          key={`item-${groupIndex}-${index}`}
          type="button"
          onClick={() => !disabled && onClick?.()}
          icon={Icon && <Icon color={intent === 'danger' && 'red'} />}
          disabled={disabled}
        >
          {name}
        </Button>
      )
    )

    if (buttons.length === 1) {
      return buttons[0]
    }

    return <ButtonGroup key={`group-${groupIndex}`}>{buttons}</ButtonGroup>
  })
}

export const HeaderBlock: React.FC<Props> = ({
  header,
  meta,
  subHeader,
  menuItems,
  contentInset,
  withDivider = false,
  badges
}) => {
  const floatingDetailControls = useFloatingDetailControls()
  const registerCustomCloseButton = floatingDetailControls?.registerCustomCloseButton
  const showFloatingCloseButton = Boolean(floatingDetailControls?.isOverlayCardVisible)

  useLayoutEffect(() => {
    if (!showFloatingCloseButton || !registerCustomCloseButton) return
    return registerCustomCloseButton()
  }, [showFloatingCloseButton, registerCustomCloseButton])

  const secondaryContent = meta ?? subHeader
  const dropdownItems = buildDropdownItems(menuItems)

  return (
    <StyledHeaderBlock $withDivider={withDivider} $hasSecondary={Boolean(secondaryContent)}>
      <HeaderWrapper $contentInset={contentInset}>
        {header && <StyledHeader $emphasized={withDivider}>{header}</StyledHeader>}
        {secondaryContent && <StyledMeta $emphasized={withDivider}>{secondaryContent}</StyledMeta>}
        {badges && <StyledBadgesRow>{badges}</StyledBadgesRow>}
      </HeaderWrapper>

      {(showFloatingCloseButton || dropdownItems.length > 0) && (
        <ActionsWrapper $alignCenter={!secondaryContent}>
          {dropdownItems.length > 0 && (
            <DropdownMenu items={dropdownItems}>
              <Button
                data-test="invoice-actions-dropdown-button"
                isSecondary={true}
                icon={<FaEllipsisH />}
              />
            </DropdownMenu>
          )}
          {showFloatingCloseButton && (
            <Button
              data-test="floating-close-button"
              type="button"
              isSecondary={true}
              icon={<FaXmark />}
              onClick={floatingDetailControls?.onClose}
            />
          )}
        </ActionsWrapper>
      )}
    </StyledHeaderBlock>
  )
}

const StyledHeaderBlock = styled.div<{ $withDivider?: boolean; $hasSecondary?: boolean }>`
  display: flex;
  align-items: ${({ $hasSecondary }) => ($hasSecondary ? 'flex-start' : 'center')};
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme, $withDivider }) =>
    $withDivider ? '1rem 1.25rem 0.85rem' : `${theme.spacing.md}rem`};
  border-bottom: ${({ theme, $withDivider }) =>
    $withDivider ? `1px solid ${theme.colors.neutralGray}` : 'none'};

  @container invoice-detail (max-width: 36rem) {
    padding: ${({ $withDivider, theme }) =>
      $withDivider ? '0.85rem 0.85rem 0.75rem' : `${theme.spacing.md}rem`};
  }

  @media (max-width: 48em) {
    padding: ${({ $withDivider, theme }) =>
      $withDivider ? '0.85rem 0.85rem 0.75rem' : `${theme.spacing.md}rem`};
  }
`

const HeaderWrapper = styled.div<{ $contentInset?: 'md' }>`
  flex: 1;
  min-width: 0;
  padding-inline-start: ${({ theme, $contentInset }) =>
    $contentInset === 'md' ? theme.spacing.md : 0}rem;
`

const StyledHeader = styled.h2<{ $emphasized?: boolean }>`
  margin: 0;
  font-weight: ${({ $emphasized }) => ($emphasized ? 700 : 'bold')};
  font-size: ${({ theme, $emphasized }) => ($emphasized ? `${theme.fontSize.xl}rem` : undefined)};
  letter-spacing: ${({ $emphasized }) => ($emphasized ? '-0.01em' : undefined)};
  line-height: ${({ $emphasized }) => ($emphasized ? 1.25 : undefined)};
  color: ${({ theme, $emphasized }) =>
    $emphasized ? theme.colors.menuBg : theme.colors.neutralBlack};
`

const StyledMeta = styled.div<{ $emphasized?: boolean }>`
  margin-top: ${({ theme, $emphasized }) => ($emphasized ? '0.375rem' : `${theme.spacing.xs}rem`)};
  min-width: 0;
  font-size: ${({ theme, $emphasized }) => ($emphasized ? `${theme.fontSize.sm}rem` : undefined)};
  color: ${({ theme, $emphasized }) => ($emphasized ? theme.dashboard.textSubtle : undefined)};
`

const StyledBadgesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  margin-top: ${({ theme }) => theme.spacing.md}rem;
  margin-bottom: ${({ theme }) => theme.spacing.xxs}rem;
`

const ActionsWrapper = styled.div<{ $alignCenter?: boolean }>`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  align-items: ${({ $alignCenter }) => ($alignCenter ? 'center' : 'flex-start')};
`
